import React from 'react';

const MatrimonyNotFound = () => {
  return (
    <div className="relative min-h-screen bg-black text-white font-sans flex items-center justify-center p-6 overflow-hidden">
      
      {/* --- BACKGROUND ATMOSPHERE --- */}
      
      {/* 1. Gradient Orbs (Soft Lighting) */}
      <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-pink-900 rounded-full mix-blend-screen filter blur-[128px] opacity-20"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-purple-900 rounded-full mix-blend-screen filter blur-[128px] opacity-20"></div>

      {/* 2. Floating Background Hearts (Animated) */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[10%] left-[20%] text-pink-500 opacity-10 text-4xl animate-float-slow">♥</div>
        <div className="absolute top-[30%] right-[15%] text-pink-400 opacity-10 text-6xl animate-float-medium">♥</div>
        <div className="absolute bottom-[20%] left-[10%] text-purple-500 opacity-10 text-5xl animate-float-fast">♥</div>
        <div className="absolute bottom-[40%] right-[30%] text-pink-600 opacity-05 text-8xl animate-float-slow">♥</div>
      </div>

      {/* --- MAIN CONTENT CARD --- */}
      <div className="relative z-10 max-w-4xl w-full text-center">
        
        {/* Glassmorphism Container */}
        <div className="backdrop-blur-xl bg-white/5 border border-pink-500/20 rounded-[2rem] p-12 md:p-16 shadow-[0_0_60px_-15px_rgba(236,72,153,0.15)] animate-fade-in-up">
          
          {/* THE 404 VISUAL */}
          <div className="flex justify-center items-center gap-4 mb-6">
            {/* The 4 */}
            <span className="text-[8rem] md:text-[10rem] font-bold text-transparent bg-clip-text bg-gradient-to-b from-white to-gray-600 drop-shadow-2xl">
              4
            </span>
            
            {/* The 0: RADAR SCANNER ANIMATION */}
            <div className="relative w-32 h-32 md:w-40 md:h-40 flex items-center justify-center rounded-full border border-pink-500/30 bg-black/50 shadow-[0_0_30px_rgba(236,72,153,0.2)]">
               {/* Radar Sweep Line */}
               <div className="absolute inset-0 rounded-full bg-gradient-to-b from-pink-500/50 to-transparent w-full h-1/2 origin-bottom animate-radar-spin z-10"></div>
               {/* Center Dot */}
               <div className="absolute w-3 h-3 bg-pink-500 rounded-full shadow-[0_0_10px_#ec4899] z-20"></div>
               {/* Broken Heart Icon inside Radar */}
               <div className="absolute z-0 opacity-40">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-pink-700" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                  </svg>
                  {/* Crack Line */}
                  <div className="absolute top-0 left-1/2 w-1 h-full bg-black/60 rotate-12 transform -translate-x-1/2"></div>
               </div>
            </div>

            {/* The 4 */}
            <span className="text-[8rem] md:text-[10rem] font-bold text-transparent bg-clip-text bg-gradient-to-b from-white to-gray-600 drop-shadow-2xl">
              4
            </span>
          </div>

          {/* TEXT CONTENT */}
          <h2 className="text-3xl md:text-5xl font-bold mb-4 text-white tracking-wide">
            Page Not Found
          </h2>
          
          <p className="text-pink-200/80 text-lg md:text-xl mb-10 max-w-lg mx-auto leading-relaxed">
            We searched high and low, but the page you are looking for seems to have broken up with us.
          </p>

          {/* CALL TO ACTION BUTTON */}
          <button 
            onClick={() => window.location.href = '/'}
            className="group relative inline-flex items-center justify-center gap-3 px-10 py-4 bg-gradient-to-r from-pink-600 to-purple-600 rounded-full font-semibold text-white tracking-wider shadow-[0_10px_20px_rgba(236,72,153,0.4)] transition-all duration-300 hover:scale-105 hover:shadow-[0_0_40px_rgba(236,72,153,0.6)] animate-heartbeat"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 group-hover:animate-ping" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            Find A Better Match
          </button>

        </div>
      </div>

      {/* --- ANIMATION STYLES --- */}
      <style>{`
        /* Radar Spin */
        @keyframes radar-spin {
          from { transform: rotate(0deg); opacity: 0.5; }
          to { transform: rotate(360deg); opacity: 0.5; }
        }
        .animate-radar-spin {
          animation: radar-spin 2s linear infinite;
        }

        /* Heartbeat for Button */
        @keyframes heartbeat {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        .animate-heartbeat {
          animation: heartbeat 2s ease-in-out infinite;
        }

        /* Floating Hearts */
        @keyframes float {
          0% { transform: translateY(0px) rotate(0deg); opacity: 0.1; }
          50% { transform: translateY(-20px) rotate(10deg); opacity: 0.3; }
          100% { transform: translateY(0px) rotate(0deg); opacity: 0.1; }
        }
        .animate-float-slow { animation: float 6s ease-in-out infinite; }
        .animate-float-medium { animation: float 4s ease-in-out infinite; }
        .animate-float-fast { animation: float 3s ease-in-out infinite; }

        /* Fade In Up */
        .animate-fade-in-up {
          animation: fadeInUp 0.8s ease-out forwards;
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

    </div>
  );
};

export default MatrimonyNotFound;